import React, { useEffect, useState, useCallback, useMemo } from 'react';

// Styles & Assets
import styles from '../../../styles/Columns.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';
import { areFrequenciesEqual, getPollFrequency } from '../../../services/Poll';
import { parseReminderTime } from '../../../services/util';

// Hooks
import { useTranslation } from 'react-i18next';
import useHttp from '../../../hooks/useHttp';

// Components
import TableConstants from '../TableConstants/TableConstants';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import SelectFrequency from '../../Helpers/SelectFrequency/SelectFrequency';
import HourSelector from '../../UI/HourSelector/HourSelector';
import BlueCell from '../BlueCell/BlueCell';
import AutocompletePreconfig from '../../Helpers/AutocompletePreconfig/AutocompletePreconfig';

// Interfaces
import { PatientInquiry, Poll, PollFrequency } from '../../../interfaces/Poll';
import { Column } from '../../../interfaces/Columns';
import { OptionAutocomplete } from '../../../interfaces/Input';
import { HH, mm } from '../../../interfaces/Time';
import { PreconfigInquiry } from '../../../interfaces/Preconfigurations';

interface SelectedPoll extends PollFrequency, Pick<PatientInquiry,
    'reminder_time' |
    'modifed_frequency_by_preconfig' |
    'modifed_reminder_by_preconfig' |
    'modified_frequency' |
    'modified_reminder'
> {
    id: number;
    name: Poll['name'];
}

interface Props {
    compareInquiries?: PreconfigInquiry[];
    preconfigInquiries: Omit<PatientInquiry, 'patient_id' | 'start_date'>[];
    handleChangeInquiry: (oldID: number, inquiryID: number, option: OptionAutocomplete | null, poll: Poll | undefined) => void;
    handleChangeInquiryFreq: (id: number, freq: PollFrequency) => void;
    handleChangeInquiryReminder: (id: number, reminder: string | null) => void;
}

const PreconfigPollTable: React.FC<Props> = ({
    compareInquiries,
    preconfigInquiries,
    handleChangeInquiry,
    handleChangeInquiryFreq,
    handleChangeInquiryReminder
}) => {
    const { t } = useTranslation();

    const [polls, setPolls] = useState<Poll[]>([]);
    const { isLoading, error, sendRequest: getPolls } = useHttp<Poll[]>();

    useEffect(() => {
        getPolls({
            url: getEnvironmentApiUrl() + 'inquiry/all',
            method: 'POST',
            body: {
                params: {},
                filter: { enabled: true, modified: false, deleted: false }
            },
        }, (data) => setPolls(data));
    }, [getPolls]);

    const handleChangePoll = (id: number) => (_: unknown, option: OptionAutocomplete | null) => {
        const poll = polls.find(p => p.id === option?.id);
        handleChangeInquiry(id, poll?.id ?? -2, option, poll);
    };

    const handleChangeFreq = (id: number) => (freq: PollFrequency) => {
        handleChangeInquiryFreq(id, freq);
    };

    const handleChangeReminder = (id: number) => (hours: HH | null, minutes: mm | null) =>
        handleChangeInquiryReminder(id, parseReminderTime(hours, minutes));

    const inquiriesRows = useMemo<SelectedPoll[]>(() => preconfigInquiries.map(i => {
        const poll = polls.find(p => p.id === i.inquiry_id);

        return {
            id: i.inquiry_id,
            name: poll?.name ?? '',
            ...getPollFrequency(i),
            modifed_frequency_by_preconfig: i.modifed_frequency_by_preconfig,
            reminder_time: i.reminder_time ?? null,
            modifed_reminder_by_preconfig: i.modifed_reminder_by_preconfig,
            modified_frequency: i.modified_frequency,
            modified_reminder: i.modified_reminder
        };
    }), [polls, preconfigInquiries]);

    const pollsOptions: OptionAutocomplete[] = useMemo(() => {
        return polls.map(p => ({ id: p.id ?? -5, label: p.name ?? '' }));
    }, [polls]);

    const rowsWithoutDuplicates = Array.from(
        new Map(inquiriesRows.map(row => [row.id, row])).values()
    );

    const ROWS: SelectedPoll[] = [
        ...rowsWithoutDuplicates,
        {
            id: -1,
            name: '',
            frecuency: null,
            frequency_duration: null,
            modifed_frequency_by_preconfig: undefined,
            modifed_reminder_by_preconfig: undefined,
            modified_frequency: undefined,
            modified_reminder: undefined,
            monthly_frequency_day: null,
            reminder_time: null,
            semanal_frequency: null
        }
    ];

    const styleSelects = {
        height: '35px',
        outline: 'none',
        padding: '8px 16px 8px 6px',
        fontSize: '0.8rem'
    };

    const showFrequencyBlue = (poll: SelectedPoll) => {
        if (poll.id < 0 || compareInquiries === undefined) return false;

        const preconfigPoll = compareInquiries.find(i => i.inquiry_id === poll.id);
        if (!preconfigPoll) return true;

        return !areFrequenciesEqual(poll, preconfigPoll);
    };

    const showReminderBlue = (poll: SelectedPoll) => {
        if (poll.id < 0 || compareInquiries === undefined) return false;

        const preconfigPoll = compareInquiries.find(i => i.inquiry_id === poll.id);
        if (!preconfigPoll) return true;

        return (poll.reminder_time ?? null) !== (preconfigPoll.reminder_time ?? null);
    };

    const isNewInquiry = useCallback((inquiryID: SelectedPoll['id']) => {
        if (inquiryID < 0) return false;
        if (compareInquiries === undefined) return false;

        return (
            !compareInquiries.some(i => i.inquiry_id === inquiryID) &&
            preconfigInquiries.some(i => i.inquiry_id === inquiryID)
        );
    }, [compareInquiries, preconfigInquiries]);

    const COLUMNS: Column<SelectedPoll>[] = [
        {
            id: 0,
            name: () => t('polls'),
            visible: true,
            sortable: false,
            element: (poll) =>
                <BlueCell show={isNewInquiry(poll.id)}>
                    <AutocompletePreconfig
                        options={pollsOptions}
                        value={pollsOptions.find(o => o.id === poll.id)}
                        placeholder={t('select_poll') as string}
                        getOptionDisabled={(o) => preconfigInquiries.some(i => i.inquiry_id === o.id)}
                        handleChangeConstant={handleChangePoll(poll.id)}
                    />
                </BlueCell>
        },
        {
            id: 1,
            name: () => t('frequency'),
            visible: true,
            sortable: false,
            element: poll =>
                <BlueCell show={isNewInquiry(poll.id) || showFrequencyBlue(poll)}>
                    <SelectFrequency
                        pollFrequency={{
                            frecuency: poll.frecuency,
                            monthly_frequency_day: poll.monthly_frequency_day,
                            semanal_frequency: poll.semanal_frequency,
                            frequency_duration: poll.frequency_duration
                        }}
                        handleChangeFreq={handleChangeFreq(poll.id)}
                        style={styleSelects}
                    />
                </BlueCell>
        },
        {
            id: 2,
            name: () => t('reminder'),
            visible: true,
            sortable: false,
            element: poll =>
                <BlueCell show={isNewInquiry(poll.id) || showReminderBlue(poll)}>
                    <HourSelector
                        hours={poll.reminder_time?.slice(0, 2) as HH || null}
                        minutes={poll.reminder_time?.slice(3, 5) as mm || null}
                        handleChange={handleChangeReminder(poll.id)}
                        style={{ border: 'none' }}
                    />
                </BlueCell>,
            classBody: styles.reminderCell
        },
    ];

    return (
        <ErrorSpinnerWrapper loading={isLoading} error={error}>
            <TableConstants
                rows={ROWS}
                columns={COLUMNS}
            />
        </ErrorSpinnerWrapper>
    );
};

export default PreconfigPollTable;